import { ALLOWED_DOCUMENT_EXTENSIONS } from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

// Écran 004 : consigner une fiche question émise hors ÉtudesCarré.
// L'auteur est un employé du contrat auteur, pas forcément celui qui saisit.

type FicheQuestionFormData = {
  numero: string;
  objet: string;
  dateEmission: string;
  resume: string;
};

type Option = { value: string; label: string };

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";
const readOnlyClass =
  "w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";
const acceptedExtensions = ALLOWED_DOCUMENT_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");

export type FicheQuestionFormProps = {
  onClose: () => void;
};

export function FicheQuestionForm({ onClose }: FicheQuestionFormProps) {
  const { yard, employee, contracts } = useYardContext();
  const travauxContracts = useMemo(
    () => contracts.filter((c) => c.type === "travaux_n_1"),
    [contracts],
  );
  const [authorContractId, setAuthorContractId] = useState<number | null>(
    travauxContracts.length === 1 ? travauxContracts[0].id : null,
  );
  const [lotId, setLotId] = useState<string | null>(null);
  const [lots, setLots] = useState<Option[] | "error">([]);
  // Employés actifs de chaque contrat du chantier, par id de contrat.
  const [employeesByContract, setEmployeesByContract] = useState<
    Map<number, Option[]> | "error"
  >(new Map());
  const [authorEmployeeId, setAuthorEmployeeId] = useState(employee.id);
  const [ficheFile, setFicheFile] = useState<File | null>(null);
  const [pjFiles, setPjFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FicheQuestionFormData>({
    defaultValues: { numero: "", objet: "", dateEmission: "", resume: "" },
  });

  useEffect(() => {
    let cancelled = false;
    // ponytail: the intervention matrix is the only yard-member endpoint
    // listing lots; add a /lots route if its payload becomes a problem.
    apiClient
      .GET("/api/yards/{yardId}/intervention-matrix", {
        params: { path: { yardId: String(yard.id) } },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setLots(
          isNil(data)
            ? "error"
            : data.data.lots.map((lot) => ({
                value: String(lot.id),
                label: [lot.nLot, lot.shortName ?? lot.name]
                  .filter(isNotNil)
                  .join(" — "),
              })),
        );
      })
      .catch(() => {
        if (!cancelled) setLots("error");
      });
    return () => {
      cancelled = true;
    };
  }, [yard.id]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .GET("/api/yards/{yardId}/contracts", {
        params: { path: { yardId: String(yard.id) } },
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (isNil(data)) {
          setEmployeesByContract("error");
          return;
        }
        setEmployeesByContract(
          new Map(
            data.data.contracts.map((c) => [
              c.id,
              c.employeeContracts
                .filter((ec) => ec.isActive)
                .map((ec) => ({
                  value: ec.employeeId,
                  label:
                    [ec.firstName, ec.lastName]
                      .filter(isNotNil)
                      .join(" ")
                      .trim() || ec.employeeId,
                }))
                .sort((a, b) => a.label.localeCompare(b.label, "fr")),
            ]),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setEmployeesByContract("error");
      });
    return () => {
      cancelled = true;
    };
  }, [yard.id]);

  const authorOptions =
    employeesByContract === "error" || isNil(authorContractId)
      ? []
      : (employeesByContract.get(authorContractId) ?? []);
  // Changer de contrat auteur vide l'auteur s'il n'est pas employé du nouveau.
  const selectedAuthor = authorOptions.some((o) => o.value === authorEmployeeId)
    ? authorEmployeeId
    : null;

  const contractLabel = (c: (typeof contracts)[number]) =>
    `${c.performerCompanyName}${isNotNil(c.code) ? ` (${c.code})` : ""}`;

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isNil(authorContractId)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez le contrat auteur",
      });
      return;
    }
    if (isNil(selectedAuthor)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez l'employé auteur",
      });
      return;
    }

    const resume = data.resume.trim();
    const metadata = JSON.stringify({
      numero: data.numero.trim(),
      objet: data.objet.trim(),
      dateEmission: new Date(data.dateEmission).toISOString(),
      authorContractId,
      authorEmployeeId: selectedAuthor,
      ...(resume.length > 0 ? { resume } : {}),
      ...(isNotNil(lotId) ? { lotId: Number(lotId) } : {}),
    });

    setIsSubmitting(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/fiches-question",
        {
          params: { path: { yardId: String(yard.id) } },
          body: { metadata },
          bodySerializer: () => {
            const fd = new FormData();
            fd.append("metadata", metadata);
            if (isNotNil(ficheFile)) fd.append("fiche", ficheFile);
            for (const pj of pjFiles) fd.append("pj", pj);
            return fd;
          },
        },
      );

      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Échec de l'enregistrement",
        });
        return;
      }

      toaster.success({
        title: "Succès",
        description: "Fiche question enregistrée",
      });
      onClose();
    } catch (err) {
      console.error("Failed to record fiche question", err);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <span className={labelClass}>Contrat auteur</span>
        {travauxContracts.length === 1 ? (
          <p className={readOnlyClass}>{contractLabel(travauxContracts[0])}</p>
        ) : (
          <Select
            options={travauxContracts.map((c) => ({
              value: String(c.id),
              label: contractLabel(c),
            }))}
            value={isNotNil(authorContractId) ? [String(authorContractId)] : []}
            onValueChange={(details) => {
              const val = details.value[0];
              setAuthorContractId(isNotNil(val) ? Number(val) : null);
            }}
            placeholder="Sélectionnez votre contrat"
            required
          />
        )}
      </div>

      <div>
        <span className={labelClass}>Employé auteur</span>
        <Select
          options={authorOptions}
          value={isNotNil(selectedAuthor) ? [selectedAuthor] : []}
          onValueChange={(details) =>
            setAuthorEmployeeId(details.value[0] ?? "")
          }
          placeholder={
            isNil(authorContractId)
              ? "Sélectionnez d'abord le contrat auteur"
              : "Sélectionnez l'employé auteur"
          }
          required
        />
        {employeesByContract === "error" && (
          <p className="mt-1 text-xs text-red-600">
            Impossible de charger les employés du chantier.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="ficheQuestionNumero" className={labelClass}>
          N°
        </label>
        <input
          id="ficheQuestionNumero"
          type="text"
          {...form.register("numero")}
          required
          maxLength={255}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ficheQuestionObjet" className={labelClass}>
          Objet
        </label>
        <input
          id="ficheQuestionObjet"
          type="text"
          {...form.register("objet")}
          required
          maxLength={512}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ficheQuestionDate" className={labelClass}>
          Date d'émission
        </label>
        <input
          id="ficheQuestionDate"
          type="date"
          {...form.register("dateEmission")}
          required
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ficheQuestionResume" className={labelClass}>
          Résumé de la question posée
        </label>
        <textarea
          id="ficheQuestionResume"
          {...form.register("resume")}
          rows={4}
          maxLength={10000}
          className={inputClass}
        />
      </div>

      <div>
        <span className={labelClass}>Lot concerné</span>
        <Select
          options={lots === "error" ? [] : lots}
          value={isNotNil(lotId) ? [lotId] : []}
          onValueChange={(details) => setLotId(details.value[0] ?? null)}
          placeholder="Sélectionnez un lot"
        />
        {lots === "error" && (
          <p className="mt-1 text-xs text-red-600">
            Impossible de charger les lots du chantier.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="ficheQuestionFichier" className={labelClass}>
          Fichier de la fiche
        </label>
        <input
          id="ficheQuestionFichier"
          type="file"
          accept={acceptedExtensions}
          onChange={(e) => setFicheFile(e.target.files?.[0] ?? null)}
          className={fileInputClass}
        />
      </div>

      <div>
        <label htmlFor="ficheQuestionPj" className={labelClass}>
          Pièces jointes
        </label>
        <input
          id="ficheQuestionPj"
          type="file"
          multiple
          accept={acceptedExtensions}
          onChange={(e) => setPjFiles(Array.from(e.target.files ?? []))}
          className={fileInputClass}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" outline onClick={onClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "En cours..." : "Enregistrer la fiche question"}
        </Button>
      </div>
    </form>
  );
}
